/**
 * Cliente HTTP centralizado para el backend Velt Intrepid (patrón Care).
 *
 * Responsabilidades:
 *   - Inyecta `Authorization: Bearer <access_token>` cuando hay sesión.
 *   - Intercepta 401 → llama /api/auth/refresh → reintenta la request original (una sola vez).
 *   - Si el refresh falla, limpia sesión y dispatcha un evento global ('auth:logout') que el
 *     AuthProvider escucha para forzar redirect a /login.
 *   - Errores backend en formato { error: { code, message, details? } }.
 */

import { tokenStore } from '../auth/tokenStore'

// Default a '/api' (relativo) — el dev server de Vite proxiea a backend y producción
// sirve frontend+backend bajo el mismo dominio. Evita mixed-content en HTTPS dev.
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api'

export type ApiError = {
  code: string
  message: string
  details?: Record<string, unknown>
}

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly apiError: ApiError,
  ) {
    super(apiError.message)
    this.name = 'ApiClientError'
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** Si true, no intenta refresh en 401 (útil para evitar recursión desde /auth/refresh). */
  skipAuthRefresh?: boolean
  /** Si true, no inyecta el Bearer (útil para endpoints públicos). */
  skipAuth?: boolean
}

// Cuando un refresh está en curso, todas las requests que fallan con 401 esperan a su resultado
// para evitar disparar múltiples refresh en paralelo.
let refreshInFlight: Promise<boolean> | null = null

async function attemptRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const refreshToken = tokenStore.getRefreshToken()
    if (!refreshToken) return false

    // El endpoint de refresh depende de quién es el dueño del refresh token: el server
    // valida el hash contra `sessions.audience`, así que pegar al wrong endpoint da 401.
    const user = tokenStore.getUser()
    const refreshPath = user?.audience === 'admin' ? '/admin/auth/refresh' : '/auth/refresh'

    try {
      const response = await fetch(`${API_BASE_URL}${refreshPath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
      if (!response.ok) {
        return false
      }
      const data = (await response.json()) as {
        access_token: string
        refresh_token: string
        user: import('../auth/tokenStore').StoredUser
      }
      tokenStore.setSession(data.access_token, data.refresh_token, data.user)
      return true
    } catch {
      return false
    }
  })()

  try {
    return await refreshInFlight
  } finally {
    refreshInFlight = null
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`
  const headers: Record<string, string> = {
    Accept: 'application/json',
  }

  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
  if (options.body !== undefined && !isFormData) {
    headers['Content-Type'] = 'application/json'
  }
  // Cuando es FormData, dejamos que el browser configure Content-Type con boundary.

  if (!options.skipAuth) {
    const token = tokenStore.getAccessToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
  }

  const buildBody = (): BodyInit | undefined => {
    if (options.body === undefined) return undefined
    if (isFormData) return options.body as FormData
    return JSON.stringify(options.body)
  }

  const doFetch = () =>
    fetch(url, {
      method: options.method ?? 'GET',
      headers,
      body: buildBody(),
      signal: options.signal,
      credentials: 'include',
    })

  let response = await doFetch()

  // Si el access token expiró, intentar refresh una vez y reintentar.
  if (response.status === 401 && !options.skipAuthRefresh && !options.skipAuth) {
    const refreshed = await attemptRefresh()
    if (refreshed) {
      const newToken = tokenStore.getAccessToken()
      if (newToken) headers['Authorization'] = `Bearer ${newToken}`
      response = await doFetch()
    } else {
      // Refresh falló: limpiar y notificar.
      tokenStore.clear()
      window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'refresh_failed' } }))
    }
  }

  if (!response.ok) {
    let apiError: ApiError = { code: 'unknown_error', message: response.statusText }
    try {
      const payload = await response.json()
      if (payload?.error) apiError = payload.error
    } catch {
      // body no era JSON
    }
    throw new ApiClientError(response.status, apiError)
  }

  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}

export const apiClient = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
